import { HubConnectionBuilder } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';
import { create } from 'zustand/react';
import { ApiEndpoints } from '../../../constants/endpoints/apiEndpoints';
import { useAgentDrawerStore } from '../stores/agentDrawerStore';
import { AgentDrawerModel } from '../types/types';
import { batchUpdatesRef, EventType } from '../../../features/agentInbox/utils/worker/UpdateBatcher';
import { InvokeRpc } from '../../../util/hubs/types';
import { useEffect } from 'react';

type AgentDrawerRpcServer = {};

type AgentDrawerHubStore = {
  isConnected: boolean;
  isUpdating: boolean;
  isRemoving: boolean;
  invoke: InvokeRpc<AgentDrawerRpcServer> | null;
  setInvoke: (cb: InvokeRpc<AgentDrawerRpcServer>) => void;
  setIsUpdating: (value: boolean) => void;
};

export const useAgentDrawerHubStore = create<AgentDrawerHubStore>((set) => ({
  isConnected: false,
  isUpdating: false,
  isRemoving: false,
  invoke: null,
  setInvoke: (invoke) => set({ invoke, isConnected: true }),
  setIsUpdating: (value: boolean) => set({ isUpdating: value })
}));

export const AgentDrawerHub = () => {
  useEffect(() => {
    const rpc = {
      OnUpdate: (item: AgentDrawerModel) => {
        useAgentDrawerHubStore.getState().setIsUpdating(true);
        const worker = batchUpdatesRef.getInstance();
        worker.postMessage({
          type: EventType.AgentUpdate,
          data: item
        });
      },
      OnRemove: (itemId: number) => {
        useAgentDrawerHubStore.getState().setIsUpdating(true);
        const worker = batchUpdatesRef.getInstance();
        worker.postMessage({
          type: EventType.AgentRemove,
          data: { Id: itemId }
        });
      }
    };

    const connection = new HubConnectionBuilder()
      .withUrl(ApiEndpoints.hubs.agentDrawer)
      .withAutomaticReconnect([0, 2000, 10000, 30000, 60000, 120000])
      .build();

    const functions = Object.keys(rpc);
    for (const procedure of functions) {
      connection.on(procedure, (...args: any[]) => {
        (rpc[procedure as keyof typeof rpc] as any)(...args);
      });
    }

    connection
      .start()
      .then(async () => {
        const hubStore = useAgentDrawerHubStore.getState();

        hubStore.setInvoke((method, ...args) => {
          return connection.invoke(method, ...args);
        });

        await useAgentDrawerStore.getState().init();
      })
      .catch((e) => {
        Sentry.captureException(e);
      });

    connection.onreconnecting(() => {
      useAgentDrawerHubStore.getState().setIsUpdating(true);
    });
    connection.onreconnected(() => {
      useAgentDrawerStore.getState().init().then();
    });

    return () => {
      connection.stop().then();
    };
  }, []);

  return <></>;
};
